import { UnionFromArray } from '@constants/types';

export enum UserStatus {
  Active = 'active',
  Invited = 'invited',
  InviteExpired = 'invite-expired',
  Disabled = 'disabled',
}

export enum UserRoles {
  // Qnous roles
  SuperAdmin = 'super-admin',
  QmsArchitect = 'qms-architect',
  QmsModeller = 'qms-modeller',

  // Enterprise roles

  EnterpriseAdmin = 'enterprise-admin',
  ClientModeller = 'client-modeller',
  Contributor = 'contributor',
  Viewer = 'viewer',
}

export const UserStatusesWithoutDisabled = Object.values(UserStatus).filter(
  (status) => status !== UserStatus.Disabled,
);

export const ConsultantsRoles = [
  UserRoles.SuperAdmin,
  UserRoles.QmsArchitect,
  UserRoles.QmsModeller,
];

export const ProjectUserRoles = [
  UserRoles.Viewer,
  UserRoles.ClientModeller,
  UserRoles.Contributor,
  UserRoles.EnterpriseAdmin,
];

export const ReadableUserStatuses = {
  [UserStatus.Active]: 'Active',
  [UserStatus.Invited]: 'Invitation sent',
  [UserStatus.InviteExpired]: 'Invitation expired',
  [UserStatus.Disabled]: 'Deactivated',
};

export const ReadableUserRoles = {
  [UserRoles.SuperAdmin]: 'Super admin',
  [UserRoles.QmsArchitect]: 'Architect',
  [UserRoles.QmsModeller]: 'Enterprise modeller',
  [UserRoles.EnterpriseAdmin]: 'Enterprise admin',
  [UserRoles.ClientModeller]: 'Modeller',
  [UserRoles.Contributor]: 'Contributor',
  [UserRoles.Viewer]: 'Viewer',
};

export const ReadableEnterpriseUserRoles = {
  [UserRoles.EnterpriseAdmin]: 'Enterprise admin',
  [UserRoles.ClientModeller]: 'Modeller',
  [UserRoles.Contributor]: 'Contributor',
  [UserRoles.Viewer]: 'Viewer',
};

export const QmsRoles = [
  UserRoles.SuperAdmin,
  UserRoles.QmsArchitect,
  UserRoles.QmsModeller,
];

export const EnterpriseRoles = [
  UserRoles.EnterpriseAdmin,
  UserRoles.ClientModeller,
  UserRoles.Contributor,
  UserRoles.Viewer,
] as const;

/*
 * Object with configuration for the roles permissions or elements visibility based on the user role
 */
export const RolesPermissionsOrElements: Record<string, UserRoles[]> = {
  SidebarProject: EnterpriseRoles as any as UserRoles[],
  AddUserOrShareProjectButtons: [UserRoles.SuperAdmin, UserRoles.QmsArchitect],
  AddUserToProjectButton: [
    UserRoles.QmsModeller,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  EnterpriseAndProjectSettingsTabs: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
    UserRoles.Contributor,
  ],
  EditEnterpriseSettings: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  EditOrganizationTier: [UserRoles.SuperAdmin],
  EditProjectConsultants: [UserRoles.SuperAdmin, UserRoles.QmsArchitect],
  EditProjectSettings: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  EditApiKeys: [UserRoles.SuperAdmin, UserRoles.EnterpriseAdmin],
  SeeProjectSettings: [UserRoles.Contributor],
  StandardHeaderShareProjectButton: [
    UserRoles.SuperAdmin,
    UserRoles.QmsArchitect,
  ],
  StandardHeaderAddUserButton: [
    UserRoles.QmsModeller,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  UploadFAQFile: [UserRoles.SuperAdmin],
  ProjectsAssigneeFilter: [UserRoles.SuperAdmin, UserRoles.QmsArchitect],
  CreateProject: [UserRoles.SuperAdmin, UserRoles.QmsArchitect],
  ProjectActionsMenu: [...QmsRoles],
  ProjectActionsMenuAllItems: [UserRoles.SuperAdmin, UserRoles.QmsArchitect],
  ProjectActionsMenuExportItem: QmsRoles,
  EditProjectStatus: [
    UserRoles.SuperAdmin,
    UserRoles.QmsArchitect,
    UserRoles.QmsModeller,
  ],
  EditMainModel: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  CreateScenario: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
    UserRoles.Contributor,
  ],
  EditScenarios: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
    UserRoles.Contributor,
  ],
  PromoteScenario: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  DuplicateScenario: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
    UserRoles.Contributor,
  ],
  DeletePublicScenario: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  ScenarioActionsColumn: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
    UserRoles.Contributor,
  ],
  RestoreVersion: [
    ...QmsRoles,
    UserRoles.ClientModeller,
    UserRoles.EnterpriseAdmin,
  ],
  SidebarSuperAdminItems: [UserRoles.SuperAdmin],
  ProjectActivityHistory: [UserRoles.SuperAdmin, UserRoles.EnterpriseAdmin],
  BusinessUnitProfileInput: EnterpriseRoles as any as UserRoles[],

  ExportProject: [UserRoles.SuperAdmin],
  ImportProject: [UserRoles.SuperAdmin],

  CopyModel: [UserRoles.SuperAdmin],
};

export type EnterpriseRolesType = UnionFromArray<typeof EnterpriseRoles>;
